import { CitationDocumentCard } from '@/features/end-of-lease/CitationDocumentCard';
import { ReferenceDocumentCard } from '@/features/end-of-lease/ReferenceDocumentCard';
import ActivityCard from '@/features/end-of-lease/activity-card';
import {
    useActivities,
    useCitationsByProject,
    useComplianceDocumentCheckResults,
    useDocument,
    useUpdateDocumentStatus,
} from '@/features/end-of-lease/api';
import { CommentDialog } from '@/features/end-of-lease/comment-dialog';
import { useCommentDialog } from '@/features/end-of-lease/hooks/useCommentDialog';
import { DocumentStatus } from '@/features/end-of-lease/types';
import { DocType } from '@/models/docs';
import { createFileRoute } from '@tanstack/react-router';
import {
    ArrowLeft,
    ArrowRight,
    BookTextIcon,
    CircleX,
    Lock,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { formatDateTime } from '@/lib/utils';

// TODO: Move this to an actual index route documents/:id
export const Route = createFileRoute(
    '/lease/projects/$projectId/documents/$documentId/page',
)({
    component: ProjectDocumentTrackerPage,
});

const ACTIVITIES_LIMIT = 5;

function ProjectDocumentTrackerPage() {
    const { projectId: _projectId, documentId: _documentId } =
        Route.useParams();
    const projectId = Number(_projectId);
    const documentId = Number(_documentId);

    const { data: document } = useDocument(documentId);
    const { data: citations } = useCitationsByProject({ projectId, document });
    const { data: complianceChecks } = useComplianceDocumentCheckResults(documentId);
    const updateDocumentStatus = useUpdateDocumentStatus(documentId);

    const handleUpdateStatus = async (data: {
        status: DocumentStatus;
        comment: string;
    }) => {
        toast.promise(updateDocumentStatus.mutateAsync(data), {
            loading: 'Updating status...',
            success: 'Status updated successfully',
            error: 'Failed to update status',
        });
    };

    const {
        dialogState,
        openDialog,
        closeDialog,
        isCommentRequired,
        canSubmit,
        handleSubmit,
        handleCommentChange,
    } = useCommentDialog<
        DocumentStatus,
        { status: DocumentStatus; comment: string }
    >({
        onSubmit: handleUpdateStatus,
        requiresCommentStatuses: [DocumentStatus.requires_attention],
        createSubmitData: (status: DocumentStatus, comment: string) => ({
            status,
            comment,
        }),
    });

    const { data: activities } = useActivities({
        projectId,
        docIds: document ? [document.id] : [],
        limit: ACTIVITIES_LIMIT,
    });

    function openDoc(url: string) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    const parseDocumentType = (reference: string): DocType => {
        const ref = reference.toLowerCase();
        if (ref.startsWith('ad')) return DocType.AIRWORTHINESS_DIRECTIVE;
        if (ref.startsWith('sb')) return DocType.SERVICE_BULLETIN;
        if (ref.startsWith('eo')) return DocType.ENGINEERING_ORDER;
        return DocType.UNKNOWN_TYPE;
    };

    return (
        <div
            className="flex min-h-screen flex-col bg-slate-50"
            data-testid="LeaseProjectDocumentDetail"
        >
            <div className="flex flex-row w-full items-between justify-between">
            </div>

            <main className="flex-1 px-4 py-6">
                <div className="mx-auto max-w-7xl space-y-6">
                    {/* Page Heading & Actions */}
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-slate-900">
                            Compliance Verification
                        </h2>
                        <div className="flex gap-2 items-center">
                            <Button
                                variant="outline"
                                onClick={() => openDialog(DocumentStatus.verified)}
                                className="gap-2"
                                disabled={document?.meta?.status == DocumentStatus.verified}
                            >
                                <Lock className="size-4 text-green-500" />
                                Verify
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() =>
                                    openDialog(DocumentStatus.requires_attention)
                                }
                                className="gap-2"
                                disabled={
                                    document?.meta?.status ==
                                    DocumentStatus.requires_attention
                                }
                            >
                                <CircleX className="size-4 text-red-500" />
                                Needs Review
                            </Button>
                            {document?.presignedUrl && (
                                <Button
                                    variant="outline"
                                    className="gap-2 bg-transparent"
                                    onClick={() => openDoc(document.presignedUrl!)}
                                >
                                    Open Document
                                    <ArrowRight className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Compliance Checks Table */}
                    {complianceChecks && complianceChecks.length > 0 && (
                        <Card data-testid="ComplianceChecksTable">
                            <CardHeader className="pb-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-base font-medium text-slate-900">
                                        Compliance Checks
                                    </span>
                                    <span className="text-xs text-slate-500">
                                        {complianceChecks.length === 1 ? '1 check' : `${complianceChecks.length} checks`}
                                    </span>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                                                <th className="py-2 pr-4 font-medium">Check</th>
                                                <th className="py-2 pr-4 font-medium">
                                                    Description
                                                </th>
                                                <th className="py-2 pr-4 font-medium">Result</th>
                                                <th className="py-2 pr-4 font-medium">Message</th>
                                                <th className="py-2 font-medium">Checked</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {complianceChecks.map((c, idx) => (
                                                <tr
                                                    key={c.id}
                                                    className="border-t border-slate-100 hover:bg-slate-50"
                                                >
                                                    <td className="py-2 pr-4 font-medium text-slate-800">
                                                        {c.name}
                                                    </td>
                                                    <td className="py-2 pr-4 text-slate-600">
                                                        {c.description}
                                                    </td>
                                                    <td className="py-2 pr-4">
                                                        <span
                                                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold border ${
                                                                c.passed
                                                                    ? 'bg-green-50 text-green-700 border-green-200'
                                                                    : 'bg-red-50 text-red-700 border-red-200'
                                                            }`}
                                                        >
                                                            {c.passed ? 'Passed' : 'Failed'}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 pr-4 text-slate-600">
                                                        {c.message}
                                                    </td>
                                                    <td className="py-2 text-slate-500 tabular-nums">
                                                        {formatDateTime(c.createdAt)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                    {/* End Compliance Checks Table */}

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        {/* Left column with activities */}
                        <div className="col-span-1 space-y-6">
                            {activities && <ActivityCard activities={activities} />}
                        </div>

                        {/* Right column - Document Detail */}
                        <Card className="col-span-1 lg:col-span-2">
                            <CardHeader className="pb-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`px-3 py-1.5 rounded-lg font-semibold text-sm ${
                                                    document?.type === 'ad'
                                                        ? 'bg-red-100 text-red-800 border border-red-200'
                                                        : document?.type === 'sb'
                                                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                                          : document?.type === 'eo'
                                                            ? 'bg-green-100 text-green-800 border border-green-200'
                                                            : 'bg-gray-100 text-gray-800 border border-gray-200'
                                                }`}
                                            >
                                                {document?.type === 'ad'
                                                    ? 'AIRWORTHINESS DIRECTIVE'
                                                    : document?.type === 'sb'
                                                      ? 'SERVICE BULLETIN'
                                                      : document?.type === 'eo'
                                                        ? 'ENGINEERING ORDER'
                                                        : document?.type?.toUpperCase() ||
                                                          'DOCUMENT'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-base font-medium text-slate-900">
                                                {document?.meta?.raw?.title || 'Document Details'}
                                            </span>
                                            <p className="text-sm text-slate-600">
                                                {document?.meta?.raw?.id}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-6">
                                    {/* Document Metadata */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                                        <div>
                                            <span className="text-xs text-slate-500">
                                                Status
                                            </span>
                                            <p className="font-medium text-sm">
                                                {document?.meta?.status ||
                                                    'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-500">
                                                Document ID
                                            </span>
                                            <p className="font-medium text-sm">
                                                #{documentId}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Connected Documents Section (Incoming) */}
                                    {citations && citations.length > 0 && (
                                        <div data-testid="LeaseProjectDocumentCitations">
                                            <div>
                                                <div className="flex items-center gap-2 mb-4">
                                                    <ArrowLeft className="h-4 w-4 text-slate-500" />
                                                    <h4 className="text-base font-medium text-slate-900">
                                                        Citations
                                                    </h4>
                                                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                                                        {citations.length}{' '}
                                                        incoming
                                                    </span>
                                                </div>

                                                <div className="space-y-2">
                                                    {citations.map(
                                                        (citation) => (
                                                            <CitationDocumentCard
                                                                key={`connected-${citation.id}`}
                                                                projectId={
                                                                    projectId
                                                                }
                                                                documentId={
                                                                    citation.id
                                                                }
                                                                type={
                                                                    citation.type
                                                                }
                                                                onOpenDocument={
                                                                    openDoc
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* References Section (Outgoing) */}
                                    {document?.links &&
                                        document.links.length > 0 && (
                                            <div>
                                                <Separator />
                                                <div className="flex items-center gap-2 mb-4">
                                                    <ArrowRight className="h-4 w-4 text-slate-500" />
                                                    <h4 className="text-base font-medium text-slate-900">
                                                        References
                                                    </h4>
                                                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                                                        {document.links.length}{' '}
                                                        outgoing
                                                    </span>
                                                </div>

                                                <div className="space-y-2">
                                                    {document.links.map(
                                                        (reference, index) => (
                                                            <ReferenceDocumentCard
                                                                key={`ref-${index}`}
                                                                projectId={
                                                                    projectId
                                                                }
                                                                type={parseDocumentType(
                                                                    reference,
                                                                )}
                                                                onOpenDocument={
                                                                    openDoc
                                                                }
                                                                reference={
                                                                    reference
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                    {/* Empty state */}
                                    {(!document?.links ||
                                        document.links.length === 0) &&
                                        (!citations ||
                                            citations.length === 0) && (
                                            <div className="text-center py-8 text-slate-500">
                                                <BookTextIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                                                <p className="text-sm">
                                                    No references or connected
                                                    documents found
                                                </p>
                                            </div>
                                        )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>

            <CommentDialog
                open={dialogState.open}
                comment={dialogState.comment}
                isCommentRequired={isCommentRequired}
                canSubmit={canSubmit}
                onCancel={closeDialog}
                onSubmit={handleSubmit}
                onCommentChange={handleCommentChange}
            />
        </div>
    );
}
